import {
  RAW_MATERIAL_CATEGORIES,
  RAW_MATERIAL_CATEGORY_LABELS,
  RAW_MATERIAL_KINDS,
  RAW_MATERIAL_KIND_LABELS,
  type RawMaterial,
  type RawMaterialCategory,
} from "@artisancode/api-types";
import { Plus, Pencil, Trash2, History } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import type { Column, FilterOption } from "@/components/shared/data-table";
import { DataTable } from "@/components/shared/data-table";
import { PageHeader } from "@/components/shared/page-header";
import { StatusToggleBadge } from "@/components/shared/status-toggle-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useHasPermission, useMe } from "@/hooks/use-auth";
import { useUpdateRawMaterial } from "@/hooks/use-raw-materials";
import { useServerTable } from "@/hooks/use-server-table";
import { useWorkspaceFilterOption } from "@/hooks/use-workspace-filter";
import { useWorkspaces } from "@/hooks/use-workspaces";
import { queryKeys } from "@/lib/query-keys";
import { rawMaterialService } from "@/services/raw-material";

import { RawMaterialHistoryDialog } from "./item-history-dialog";
import { RawMaterialDeleteDialog } from "./raw-material-delete-dialog";
import { RawMaterialDialog } from "./raw-material-dialog";
import { formatCostPerUnit, rawMaterialUomLabel } from "./raw-material-utils";

const categoryOptions = RAW_MATERIAL_CATEGORIES.map((cat) => ({
  value: cat,
  label: RAW_MATERIAL_CATEGORY_LABELS[cat],
}));

const categoryFilters: FilterOption[] = [
  {
    key: "kind",
    label: "Tipe",
    options: RAW_MATERIAL_KINDS.map((k) => ({
      value: k,
      label: RAW_MATERIAL_KIND_LABELS[k],
    })),
  },
  { key: "category", label: "Kategori", options: categoryOptions },
];

export function RawMaterials() {
  const canCreate = useHasPermission("raw_materials.create");
  const canUpdate = useHasPermission("raw_materials.update");
  const canDelete = useHasPermission("raw_materials.delete");

  const { mutate: update } = useUpdateRawMaterial();
  const { data: me } = useMe();
  const { data: workspacesData } = useWorkspaces({ per_page: 100 });
  const workspaces = workspacesData?.items ?? [];

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<RawMaterial | null>(null);
  const [deleting, setDeleting] = useState<RawMaterial | null>(null);
  const [historyOf, setHistoryOf] = useState<RawMaterial | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const workspaceFilter = useWorkspaceFilterOption();
  const table = useServerTable<RawMaterial>({
    queryKey: (params) => queryKeys.rawMaterials.list(params),
    fetcher: (params) => rawMaterialService.list(params),
    pageSize: 10,
    filterKeys: [...categoryFilters.map((f) => f.key), "workspace_id"],
  });

  function toggleActive(item: RawMaterial) {
    if (!canUpdate || updatingId === item.id) return;
    setUpdatingId(item.id);
    update(
      { id: item.id, isActive: !item.isActive },
      {
        onSuccess: () =>
          toast.success(item.isActive ? "Dinonaktifkan." : "Diaktifkan."),
        onError: () => toast.error("Gagal mengubah status bahan baku."),
        onSettled: () => setUpdatingId(null),
      },
    );
  }

  function workspaceName(workspaceId: string) {
    return workspaces.find((w) => w.id === workspaceId)?.name ?? "-";
  }

  const columns: Column<RawMaterial>[] = [
    {
      key: "name",
      label: "Bahan Baku",
      render: (i) => (
        <div className="flex flex-col">
          <span className="font-medium">{i.name}</span>
          {i.sku && (
            <span className="font-mono text-xs text-muted-foreground">
              {i.sku}
            </span>
          )}
          {i.ingredient && (
            <span className="text-xs text-muted-foreground">
              Dibuka jadi {i.ingredient.name} ·{" "}
              {(i.packQty ?? 0).toLocaleString("id-ID")}{" "}
              {i.ingredient.uom?.symbol} / {rawMaterialUomLabel(i)}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "kind",
      label: "Tipe",
      render: (i) => (
        <Badge variant="outline">{RAW_MATERIAL_KIND_LABELS[i.kind]}</Badge>
      ),
    },
    {
      key: "category",
      label: "Kategori",
      render: (i) => (
        <Badge variant="secondary">
          {RAW_MATERIAL_CATEGORY_LABELS[i.category as RawMaterialCategory] ??
            i.category}
        </Badge>
      ),
    },
    {
      key: "uom",
      label: "Satuan (UOM)",
      render: (i) => (
        <span className="inline-flex items-center gap-1 font-medium text-sm">
          {i.uom?.name ?? "-"}{" "}
          {i.kind !== "purchased" && i.uom?.symbol && (
            <span className="text-muted-foreground font-mono text-xs">
              ({i.uom.symbol})
            </span>
          )}
        </span>
      ),
    },
    {
      key: "costPerUnit",
      label: "Biaya / Satuan",
      render: (i) => (
        <span className="font-medium text-sm">{formatCostPerUnit(i)}</span>
      ),
    },
    {
      key: "minStock",
      label: "Min. Stok",
      render: (i) => (
        <span className="text-sm text-muted-foreground">
          {i.minStock != null
            ? `${i.minStock.toLocaleString("id-ID")} ${rawMaterialUomLabel(i) ?? ""}`
            : "-"}
        </span>
      ),
    },
    ...(me?.canSwitchWorkspace
      ? [
          {
            key: "workspace",
            label: "Workspace",
            render: (i: RawMaterial) => (
              <span className="text-sm">{workspaceName(i.workspaceId)}</span>
            ),
          },
        ]
      : []),
    {
      key: "status",
      label: "Status",
      render: (i) => (
        <StatusToggleBadge
          isActive={i.isActive}
          isLoading={updatingId === i.id}
          disabled={!canUpdate}
          onClick={() => toggleActive(i)}
        />
      ),
    },
  ];

  function openAdd() {
    setEditing(null);
    setOpen(true);
  }

  function openEdit(item: RawMaterial) {
    setEditing(item);
    setOpen(true);
  }

  return (
    <div>
      <PageHeader
        title="Bahan Baku"
        action={
          <Button size="sm" onClick={openAdd} disabled={!canCreate}>
            <Plus className="mr-1 h-4 w-4" />
            Tambah
          </Button>
        }
      />
      <DataTable
        data={table.items}
        loadedData={table.loadedItems}
        columns={columns}
        filters={[...categoryFilters, ...workspaceFilter]}
        activeFilters={table.activeFilters}
        onFilterChange={table.onFilterChange}
        searchPlaceholder="Cari bahan baku..."
        query={table.query}
        onQueryChange={table.onQueryChange}
        page={table.page}
        totalPages={table.totalPages}
        totalCount={table.totalCount}
        onPageChange={table.onPageChange}
        hasMore={table.hasMore}
        onLoadMore={table.onLoadMore}
        loading={table.loading}
        actions={(item) => (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => openEdit(item)}
              disabled={!canUpdate}
              title="Edit bahan baku"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setHistoryOf(item)}
              title="Riwayat harga"
            >
              <History className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-destructive hover:text-destructive"
              onClick={() => setDeleting(item)}
              disabled={!canDelete}
              title="Hapus bahan baku"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      />

      <RawMaterialDialog open={open} onOpenChange={setOpen} editing={editing} />
      <RawMaterialHistoryDialog
        item={historyOf}
        onOpenChange={(v) => !v && setHistoryOf(null)}
      />
      <RawMaterialDeleteDialog
        item={deleting}
        onOpenChange={(isOpen) => !isOpen && setDeleting(null)}
      />
    </div>
  );
}
